import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, MagnifyingGlass, PencilSimple, Trash } from "@phosphor-icons/react";

const STATUSES = ["active", "pending", "archived"];
const CATEGORIES = ["general", "product", "project", "ticket"];

export default function Items() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", status: "active", category: "general", tags: "" });
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    const params = {};
    if (search) params.search = search;
    if (statusFilter !== "all") params.status = statusFilter;
    try {
      const { data } = await api.get("/items", { params });
      setItems(data.items);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const openNew = () => {
    setEditing(null);
    setForm({ title: "", description: "", status: "active", category: "general", tags: "" });
    setOpen(true);
  };

  const openEdit = (it) => {
    setEditing(it);
    setForm({
      title: it.title,
      description: it.description || "",
      status: it.status,
      category: it.category,
      tags: (it.tags || []).join(", "),
    });
    setOpen(true);
  };

  const submit = async () => {
    const payload = {
      ...form,
      tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
    };
    try {
      if (editing) {
        await api.patch(`/items/${editing.id}`, payload);
        toast.success("Item updated");
      } else {
        await api.post("/items", payload);
        toast.success("Item created");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    try {
      await api.delete(`/items/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="p-8">
      <header className="mb-6 pb-6 border-b border-[#262626] flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#71717A] mb-2">Resources</div>
          <h1 className="font-display text-4xl font-black tracking-tight">Items</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNew} className="rounded-sm bg-[#0055FF] hover:bg-[#0044CC]" data-testid="new-item-btn">
              <Plus size={14} weight="bold" className="mr-2" /> New item
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-sm bg-[#141414] border-[#262626] text-[#EDEDED]" data-testid="item-dialog">
            <DialogHeader>
              <DialogTitle className="font-display text-xl">
                {editing ? "Edit item" : "New item"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-xs uppercase tracking-wider">Title</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 rounded-sm bg-[#0A0A0A] border-[#262626]" data-testid="item-title-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Description</Label>
                <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 rounded-sm bg-[#0A0A0A] border-[#262626]" rows={3} data-testid="item-desc-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider">Status</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger className="mt-1 rounded-sm bg-[#0A0A0A] border-[#262626]" data-testid="item-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#141414] border-[#262626]">
                      {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider">Category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger className="mt-1 rounded-sm bg-[#0A0A0A] border-[#262626]" data-testid="item-category-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#141414] border-[#262626]">
                      {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Tags (comma separated)</Label>
                <Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} className="mt-1 rounded-sm bg-[#0A0A0A] border-[#262626]" data-testid="item-tags-input" />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={submit} className="rounded-sm bg-[#0055FF] hover:bg-[#0044CC]" data-testid="item-save-btn">
                {editing ? "Save" : "Create"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </header>

      <div className="flex gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search title or description..."
            className="pl-9 rounded-sm bg-[#141414] border-[#262626]"
            data-testid="search-items-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40 rounded-sm bg-[#141414] border-[#262626]" data-testid="filter-status-select"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#141414] border-[#262626]">
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button onClick={load} variant="outline" className="rounded-sm border-[#262626] bg-transparent hover:bg-[#141414]">Search</Button>
      </div>

      <div className="border border-[#262626] bg-[#0F0F0F]" data-testid="items-table">
        <Table>
          <TableHeader>
            <TableRow className="border-[#262626] hover:bg-transparent">
              <TableHead className="text-[#71717A] uppercase text-[10px] tracking-widest">Title</TableHead>
              <TableHead className="text-[#71717A] uppercase text-[10px] tracking-widest">Category</TableHead>
              <TableHead className="text-[#71717A] uppercase text-[10px] tracking-widest">Status</TableHead>
              <TableHead className="text-[#71717A] uppercase text-[10px] tracking-widest">Tags</TableHead>
              <TableHead className="text-[#71717A] uppercase text-[10px] tracking-widest text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 && !loading && (
              <TableRow className="border-[#262626]"><TableCell colSpan={5} className="text-center py-12 text-[#71717A] dashed">No items yet. Click "New item" to create one.</TableCell></TableRow>
            )}
            {items.map((it) => (
              <TableRow key={it.id} className="border-[#262626] hover:bg-[#141414]">
                <TableCell className="font-medium">{it.title}</TableCell>
                <TableCell className="text-[#A1A1AA]">{it.category}</TableCell>
                <TableCell>
                  <Badge variant="outline" className="rounded-sm border-[#262626] font-mono text-[10px] uppercase">
                    {it.status}
                  </Badge>
                </TableCell>
                <TableCell className="text-[#A1A1AA]">
                  {(it.tags || []).slice(0, 3).map((t) => (
                    <span key={t} className="mr-2 text-xs font-mono">#{t}</span>
                  ))}
                </TableCell>
                <TableCell className="text-right space-x-2">
                  <Button size="sm" variant="outline" className="rounded-sm border-[#262626] bg-transparent hover:bg-[#141414]" onClick={() => openEdit(it)} data-testid={`edit-item-${it.id}`}>
                    <PencilSimple size={12} />
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-sm border-[#262626] bg-transparent hover:bg-[#EF4444] hover:text-white" onClick={() => remove(it.id)} data-testid={`delete-item-${it.id}`}>
                    <Trash size={12} />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
